import React, { useState } from "react";
import { motion } from "framer-motion";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import {
  Check,
  Send,
  User,
  Mail,
  MessageSquare,
  Sparkles,
  Zap,
  Target
} from "lucide-react";

const ContactCTA = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const encoded = new URLSearchParams({
        "form-name": "custom-solution",
        ...formData,
      }).toString();

      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encoded,
      });

      // Clear form and show success (optional, for now just logging)
      console.log('Form submitted successfully');
      setFormData({ name: '', email: '', message: '' });
      alert('Thank you! We will get back to you soon.');
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Something went wrong. Please try again.');
    }
  };

  return (
    <section className="relative py-20 md:py-24 overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="max-w-6xl mx-auto"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            <div className="flex flex-col md:flex-row">
              {/* Left Side - Enhanced Gradient Section */}
              <motion.div
                className="md:w-1/2 relative bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 text-white p-10 md:p-12 lg:p-14 overflow-hidden"
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {/* Animated Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl animate-pulse" />
                  <div className="absolute bottom-0 left-0 w-64 h-64 bg-purple-300 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
                </div>

                <div className="relative z-10">
                  {/* Badge */}
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20"
                  >
                    <Sparkles className="w-4 h-4 text-yellow-300" />
                    <span className="text-sm font-bold uppercase tracking-wider">Custom Solutions</span>
                  </motion.div>

                  <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">
                    Need a Custom Solution?
                  </h2>
                  <p className="text-lg mb-8 opacity-95 leading-relaxed" style={{ lineHeight: '1.7' }}>
                    Our team of experts is ready to understand your unique challenges and develop tailored solutions that address your specific business needs.
                  </p>

                  {/* Features List */}
                  <ul className="space-y-4 mb-8">
                    {[
                      {
                        icon: Target,
                        text: "Personalized consultation with industry experts",
                        color: "text-yellow-300"
                      },
                      {
                        icon: Zap,
                        text: "Customized solutions based on your requirements",
                        color: "text-green-300"
                      },
                      {
                        icon: Check,
                        text: "Transparent pricing and project timelines",
                        color: "text-blue-300"
                      }
                    ].map((item, index) => (
                      <motion.li
                        key={index}
                        className="flex items-start group"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 + index * 0.1 }}
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center mr-3 mt-0.5 group-hover:bg-white/20 transition-all duration-300">
                          <item.icon className={`w-5 h-5 ${item.color}`} strokeWidth={2.5} />
                        </div>
                        <span className="text-base leading-relaxed">{item.text}</span>
                      </motion.li>
                    ))}
                  </ul>

                  {/* Trust Indicators */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.7 }}
                    className="pt-6 border-t border-white/20"
                  >
                    <div className="flex items-center gap-6 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                        <span className="text-white/90">Available 24/7</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-yellow-400" />
                        <span className="text-white/90">Quick Response</span>
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Decorative Gradient Orb */}
                <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-gradient-to-br from-purple-400/30 to-pink-400/30 rounded-full blur-3xl" />
              </motion.div>

              {/* Right Side - Enhanced Form */}
              <motion.div
                className="md:w-1/2 p-10 md:p-12 lg:p-14 bg-gradient-to-br from-white to-slate-50"
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center shadow-lg">
                    <MessageSquare className="w-6 h-6 text-white" strokeWidth={2.5} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900">Get in Touch</h3>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="space-y-6"
                  name="custom-solution"
                  method="POST"
                  data-netlify="true"
                >
                  <input type="hidden" name="form-name" value="custom-solution" />
                  {/* Name Field */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 }}
                  >
                    <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                      Full Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <User className="w-5 h-5 text-slate-400" />
                      </div>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all duration-300 text-slate-900 placeholder-slate-400"
                        placeholder="Your name"
                        required
                      />
                    </div>
                  </motion.div>

                  {/* Email Field */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 }}
                  >
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                        <Mail className="w-5 h-5 text-slate-400" />
                      </div>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all duration-300 text-slate-900 placeholder-slate-400"
                        placeholder="your.email@example.com"
                        required
                      />
                    </div>
                  </motion.div>

                  {/* Message Field */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.6 }}
                  >
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                      How can we help?
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows="4"
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition-all duration-300 text-slate-900 placeholder-slate-400 resize-none"
                      placeholder="Tell us about your project or requirements"
                      required
                    ></textarea>
                  </motion.div>

                  {/* Submit Button */}
                  <motion.div
                    className="pt-2"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.7 }}
                  >
                    <motion.div
                      whileHover={{ scale: 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Button
                        variant="primary"
                        size="lg"
                        className="w-full shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                        icon="Send"
                        iconPosition="right"
                        type="submit"
                      >
                        Send Message
                      </Button>
                    </motion.div>
                  </motion.div>

                  {/* Privacy Note */}
                  <motion.p
                    className="text-xs text-slate-500 text-center mt-4"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.8 }}
                  >
                    We respect your privacy. Your information will never be shared.
                  </motion.p>
                </form>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactCTA;
